import Pane from 'components/Common/Pane'
import { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { arePricesLoading, isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import AccommodationRoomTileDetailsVariant from '../../FlashHotel/FlashHotelMobileVariant/AccommodationRoomTileDetailsVariant'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useLPCUniqueOptions from 'hooks/OfferPage/useLPCUniqueOptions'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { initialOfferPageRooms } from 'contexts/OfferPage/offerPageStateReducer'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import AlwaysOnCheapestRateOptionMobile from './AlwaysOnCheapestRateOptionMobile'
import moment from 'moment'
import { FormattedMessage, useIntl } from 'react-intl'

const RateSection = styled.div`
  padding: ${rem(16)} ${rem(20)} ${rem(20)};
`

interface Props {
  offer: App.Offer
  packageViews: Array<App.OfferPackageOptionView>
  checkIn?: string
  rooms: Array<App.Occupants>
  previewMode: boolean
  pricesLoading?: boolean
  duration?: number
  detailsModalMode: 'room-and-package' | 'room-only'
  availableForPurchase?: boolean
}

function AlwaysOnRoomOptionMobile(props: Props) {
  const intl = useIntl()
  const {
    offer,
    packageViews,
    checkIn,
    rooms,
    previewMode,
    pricesLoading,
    duration,
    detailsModalMode,
    availableForPurchase,
  } = props

  const pkg = packageViews[0].package

  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable),
  ], [packageViews])

  const availablePackageViews = useMemo(() => {
    const requiresRates = offer.property?.useDynamicCancellationPolicies && !!checkIn && !pricesLoading
    return packageViews.filter(view => view.capacityValid && view.hotelAvailable && (!requiresRates || view.hasAvailableRates))
  }, [checkIn, offer, packageViews, pricesLoading])

  /**
   * Preview is used only by content people.
   * We don't want to show unavailable rateOptions to the user.
   */
  const options = previewMode ? packageViews : availablePackageViews
  const availableOptions = useLPCUniqueOptions(
    offer.id,
    options,
    {
      useDynamicCancellationPolicies: offer.property?.useDynamicCancellationPolicies ?? false,
      duration,
      checkIn,
      rooms: rooms ?? initialOfferPageRooms,
    },
  )

  const cheapestPackageView = useMemo(() => availableOptions.length > 0 ? availableOptions.reduce((cheapest, current) => current.hotelPrice < cheapest.hotelPrice ? current : cheapest) : undefined, [availableOptions])

  const hasMultipleCancellationPolicies = useMemo(() => {
    if (!availableOptions.length) return false
    const firstRefundable = availableOptions[0].package.roomRate?.cancellationPolicy.type !== 'non-refundable' && (!checkIn || moment(checkIn).isAfter(moment()))
    return availableOptions.some(option => {
      const refundable = option.package.roomRate?.cancellationPolicy.type !== 'non-refundable' && (!checkIn || moment(checkIn).isAfter(moment()))
      return refundable !== firstRefundable
    })
  }, [availableOptions, checkIn])

  const stayPayTier = useMemo(() => {
    return packageViews.map(view => {
      return view.hotelAvailable && view.package?.stayPay?.find(tier => tier.stayNights === duration && tier.freeNights > 0)
    }).find(Boolean)
  }, [packageViews, duration])

  if (!pkg.roomType || !cheapestPackageView) {
    return null
  }

  return (
    <>
      {checkIn && stayPayTier && <MessageBanner
        kind="promotional-primary"
        icon={<LineInfoCircleIcon />}
      >
        <FormattedMessage
          id="aAyqeo0vRbEMXbhMmgzF"
          defaultMessage="<bold>Special offer:</bold> You get {freeNightsLabel} free when staying {stayNightsLabel} on select packages."
          description="Promotional banner explaining a stay-pay offer where a required stay length earns a free-night benefit"
          values={{
            bold: (chunks) => <b>{chunks}</b>,
            freeNightsLabel: intl.formatMessage({
              id: 'YwboYDLMgMZoYZTD2XO9',
              defaultMessage: '{freeNights, plural, one {# night} other {# nights}}',
              description: 'Free night count in stay/pay promotional offer banner',
            }, { freeNights: stayPayTier.freeNights }),
            stayNightsLabel: intl.formatMessage({
              id: 'Wta4Gl7m9HkTOxGPsQrF',
              defaultMessage: '{stayNights, plural, one {# night} other {# nights}}',
              description: 'Required stay night count in stay/pay promotional offer banner',
            }, { stayNights: stayPayTier.stayNights }),
          }}
        />
      </MessageBanner>}
      <Pane border="light-foreground" borderRadius="S" data-testid="package-option">
        <AccommodationRoomTile roomPackage={pkg}>
          <AccommodationRoomTileImage
            roomPackage={pkg}
          />
          <AccommodationRoomTileDetailsVariant
            roomPackage={pkg}
            urgencyLabels={<>
              <BookingsCountLabel bookings={pkg.roomType.stats.booked} threshold="room" />
              {checkIn && <RoomsRemainingLabel roomCount={packageViews[0].availableRooms ?? 0} threshold="rate" />}
            </>}
            actions={availableForPurchase && roomFitsCapacity && datesAvailable && <CSSBreakpoint min="desktop">
              <TripGuard>
                <BookmarkButton offer={offer} roomTypeId={pkg.roomType.id} />
              </TripGuard>
            </CSSBreakpoint>}
          />
        </AccommodationRoomTile>
        <RateSection data-testid="rate-options">
          <AlwaysOnCheapestRateOptionMobile
            offer={offer}
            packageView={cheapestPackageView}
            detailsModalMode={detailsModalMode}
            hasMultipleCancellationPolicies={hasMultipleCancellationPolicies}
          />
        </RateSection>
      </Pane>
    </>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  duration: state.duration,
  previewMode: appState.system.previewMode,
  pricesLoading: arePricesLoading(appState, state),
  availableForPurchase: isAvailableForPurchase(state, appState),
}), AlwaysOnRoomOptionMobile)
